import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { pathOr, propOr } from "ramda";
import moment from "moment";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ComposedChart,
  Legend,
  Line,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { DateRangePicker } from "src/main_app/components/date_range_picker";
import { getCustomerGroupUsage } from "src/main_app/services/apis";
import { formatNumber } from "src/main_app/utils/common";
import { track } from "src/main_app/mixpanel";
import { checkPermission, useUserPermissions } from "src/main_app/actions";

import {
  TOTAL_LINE_COLOR,
  TREND_SERIES,
  UNACCOUNTED_COLOR,
  unitColor,
} from "./group-colors";

const API_DATE_FORMAT = "YYYY-MM-DD";

const round2 = (value) => Math.round((Number(value) || 0) * 100) / 100;

const kl = (value) => `${formatNumber(round2(value))} kL/d`;

const tooltipBoxStyle = {
  backgroundColor: "#fff",
  padding: "8px 12px",
  borderRadius: "4px",
  boxShadow: "0 0 8px 8px rgb(0 0 0 / 5%)",
};

const renderPieTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  const slice = payload[0];
  return (
    <div style={tooltipBoxStyle}>
      <div style={{ fontWeight: 600, color: slice.payload.color }}>
        {slice.name}
      </div>
      <div>{kl(slice.value)}</div>
    </div>
  );
};

const AGGREGATE_KEYS = ["main", "unaccounted"];

// A dozen stacked meters plus the aggregates gets busy fast, so the tooltip pins
// the group total and the unaccounted remainder to the top and then lists only
// the meters that actually used water that day.
const renderPeriodTooltip = ({ active, payload, label }) => {
  if (!active || !payload || payload.length === 0) return null;
  const aggregates = AGGREGATE_KEYS.map((key) =>
    payload.find((item) => item.dataKey === key)
  ).filter(Boolean);
  const used = payload.filter(
    (item) =>
      !AGGREGATE_KEYS.includes(item.dataKey) && Number(item.value) > 0
  );
  return (
    <div style={{ ...tooltipBoxStyle, maxHeight: "320px", overflow: "auto" }}>
      <div style={{ fontWeight: 600, marginBottom: "4px" }}>
        {moment(label).format("ddd DD MMM YYYY")}
      </div>
      {aggregates.map((item) => (
        <div
          key={item.dataKey}
          style={{ color: item.color, fontWeight: 600 }}
        >
          {item.name}:{" "}
          {item.value === null || item.value === undefined ? (
            <span className="text-muted">no reading</span>
          ) : (
            `${formatNumber(round2(item.value))} kL`
          )}
        </div>
      ))}
      {aggregates.length > 0 && used.length > 0 && (
        <div style={{ borderTop: "1px solid #eceff3", margin: "4px 0" }} />
      )}
      {used.map((item) => (
        <div key={item.dataKey} style={{ color: item.color }}>
          {item.name}: {formatNumber(round2(item.value))} kL
        </div>
      ))}
      {used.length === 0 && <div className="text-muted">No unit usage</div>}
    </div>
  );
};

const renderTrendTooltip = ({ active, payload }) => {
  if (!active || !payload || payload.length === 0) return null;
  return (
    <div style={tooltipBoxStyle}>
      <div style={{ fontWeight: 600, marginBottom: "4px" }}>
        {pathOr("", [0, "payload", "name"], payload)}
      </div>
      {payload.map((item) => (
        <div key={item.dataKey || item.name} style={{ color: item.color }}>
          {item.name}: {kl(item.value)}
        </div>
      ))}
    </div>
  );
};

const CustomerGroupPage = () => {
  const params = useParams();
  const [usage, setUsage] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  // Yesterday, not today: a meter reports through the day, so today's column is
  // always a partial reading and reads as a collapse in usage rather than as an
  // incomplete day. The window stays a week long, ending on the last full day.
  const [dateRange, setDateRange] = useState([
    moment().subtract(7, "days").toDate(),
    moment().subtract(1, "days").toDate(),
  ]);

  useEffect(() => {
    track("Customer group page");
  }, []);

  const userPermissions = useUserPermissions();
  const canViewThisPage = useMemo(
    () => checkPermission(userPermissions, "customers-page", "canView"),
    [userPermissions]
  );

  useEffect(() => {
    if (userPermissions.length > 0 && !canViewThisPage) {
      alert("You don't have permission to view this page");
      document.location.href = "/";
    }
  }, [canViewThisPage, userPermissions]);

  const loadUsage = useCallback(
    async (range) => {
      setIsLoaded(false);
      try {
        const response = await getCustomerGroupUsage(params.id, {
          from: moment(range[0]).format(API_DATE_FORMAT),
          to: moment(range[1]).format(API_DATE_FORMAT),
        });
        setUsage(pathOr(null, ["data", "data"], response));
      } catch (error) {
        console.log("error", error);
        setUsage(null);
      }
      setIsLoaded(true);
    },
    [params.id]
  );

  useEffect(() => {
    loadUsage(dateRange);
  }, [loadUsage]);

  const onApplyDateRange = useCallback(
    (event, picker) => {
      const range = [picker.startDate.toDate(), picker.endDate.toDate()];
      setDateRange(range);
      loadUsage(range);
    },
    [loadUsage]
  );

  const units = propOr([], "units", usage);
  const main = propOr({}, "main", usage);
  const unaccounted = propOr({}, "unaccounted", usage);

  // The main meter is the whole pie, so its slices are the units plus whatever
  // the units did not account for — charting "Main" as a fourth slice would
  // count the same water twice.
  const pieData = useMemo(
    () =>
      [
        ...units.map((unit, index) => ({
          name: unit.name,
          value: round2(unit.avg_daily),
          color: unitColor(index),
        })),
        {
          name: "Unaccounted for and common areas",
          value: round2(unaccounted.avg_daily),
          color: UNACCOUNTED_COLOR,
        },
      ].filter((slice) => slice.value > 0),
    [units, unaccounted]
  );

  const trendData = useMemo(
    () =>
      [main, ...units, unaccounted]
        .filter((row) => row && row.name)
        .map((row) => ({
          name: row.name,
          current_week: round2(row.current_week),
          previous_week: round2(row.previous_week),
          three_month_average: round2(row.three_month_average),
        }))
        // Recharts draws the first category at the bottom of a vertical layout;
        // reversing keeps the main meter on top, as in the reference chart.
        .reverse(),
    [main, units, unaccounted]
  );

  // Unit names are addresses and can run long; size the category axis to the
  // longest label instead of clipping it at a fixed width.
  const trendAxisWidth = useMemo(() => {
    const longest = trendData.reduce(
      (max, row) => Math.max(max, (row.name || "").length),
      0
    );
    return Math.min(Math.max(longest * 6.6 + 16, 120), 420);
  }, [trendData]);

  const series = propOr([], "series", usage);
  const parentName = propOr(
    "Main service",
    "name",
    propOr({}, "parent", usage)
  );

  // The series rows key each unit as `u_<id>`; pair those keys back up with the
  // unit names so the legend and tooltip read properly.
  const periodBars = useMemo(
    () =>
      units.map((unit, index) => ({
        dataKey: `u_${unit.id}`,
        name: unit.name,
        color: unitColor(index),
      })),
    [units]
  );

  // Three bars per row need real vertical room: a group with a dozen units
  // makes this chart much taller than the pie beside it, which is fine.
  const trendChartHeight = useMemo(
    () => Math.max(trendData.length * 46 + 80, 340),
    [trendData]
  );

  const totalAvgDaily = round2(main.avg_daily);

  if (userPermissions.length === 0 || !canViewThisPage) return null;

  return (
    <div className="the-content">
      <div className="page-heading pb-3">
        <div className="container-fluid">
          <h2 className="title mb-3">
            {propOr("Group", "name", propOr({}, "parent", usage))}
          </h2>
          <div className="block-center-between">
            <div className="left-action-menu">
              <Link className="underline" to="/customers/groups">
                <i className="fas fa-chevron-left mr-2" />
                Back to groups
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="container-fluid content">
        <div className="d-flex justify-content-end mb-3">
          <div style={{ maxWidth: "300px" }}>
            <DateRangePicker
              setting={{
                startDate: dateRange[0],
                endDate: dateRange[1],
                timePicker: false,
              }}
              onApply={onApplyDateRange}
            >
              <input type="text" className="form-control" />
            </DateRangePicker>
          </div>
        </div>

        {!isLoaded ? (
          <div className="no-item-message">Loading group usage…</div>
        ) : usage === null ? (
          <div className="no-item-message">
            This group is not available. It may have been removed or is no
            longer flagged as a multi-unit group.
          </div>
        ) : (
          <>
            <h3 className="sub-title">Usage over period</h3>

            <div className="card mb-4">
              <div className="card-body">
                {series.length === 0 ? (
                  <div className="no-item-message">
                    There is currently no usage data available.
                  </div>
                ) : (
                  <ResponsiveContainer width="100%" height={420}>
                    <ComposedChart
                      data={series}
                      margin={{ top: 20, right: 40, left: -15, bottom: 5 }}
                    >
                      <CartesianGrid stroke="#e9ebf1" />
                      <XAxis
                        dataKey="date"
                        stroke="#e9ebf1"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) =>
                          moment(value).format("DD MMM")
                        }
                      />
                      <YAxis
                        stroke="#e9ebf1"
                        tick={{ fontSize: 12 }}
                        tickFormatter={(value) => formatNumber(value)}
                        unit=" kL"
                        width={80}
                      />
                      <Tooltip
                        content={renderPeriodTooltip}
                        cursor={{ fill: "rgba(29, 42, 68, 0.04)" }}
                      />
                      <Legend iconType="circle" wrapperStyle={{ fontSize: "12px" }} />
                      {/* Units first, then the remainder: recharts stacks in
                          declaration order, so unaccounted caps the column.
                          The white stroke is the 2px gap between segments —
                          past roughly ten units colour alone stops separating
                          them, and the boundary has to be drawn. */}
                      {periodBars.map((bar) => (
                        <Bar
                          key={bar.dataKey}
                          stackId="usage"
                          dataKey={bar.dataKey}
                          name={bar.name}
                          fill={bar.color}
                          stroke="#fff"
                          strokeWidth={2}
                          maxBarSize={72}
                          isAnimationActive={false}
                        />
                      ))}
                      <Bar
                        stackId="usage"
                        dataKey="unaccounted"
                        name="Unaccounted for and common areas"
                        fill={UNACCOUNTED_COLOR}
                        stroke="#fff"
                        strokeWidth={2}
                        maxBarSize={72}
                        isAnimationActive={false}
                      />
                      {/* The main service is the master meter: `unaccounted` is
                          its reading minus the units, so the stack sums back to
                          `main` and the line traces the top of the column. It is
                          named, not labelled "Total", because a segment of its
                          own would chart the same water twice — the units are
                          already underneath it. */}
                      <Line
                        type="monotone"
                        dataKey="main"
                        name={parentName}
                        stroke={TOTAL_LINE_COLOR}
                        strokeWidth={2}
                        dot={{ r: 3, fill: TOTAL_LINE_COLOR, strokeWidth: 0 }}
                        activeDot={{ r: 5 }}
                        isAnimationActive={false}
                      />
                    </ComposedChart>
                  </ResponsiveContainer>
                )}
              </div>
            </div>

            <div className="row">
              <div className="col-md-5 mb-4">
                <div className="card">
                  <div className="card-body">
                    <h5 className="text-center mb-1">
                      Average daily water usage kL/d
                    </h5>
                    <p className="text-center text-muted small mb-3">
                      Main meter {kl(totalAvgDaily)} ·{" "}
                      {moment(dateRange[0]).format("DD MMM YYYY")} –{" "}
                      {moment(dateRange[1]).format("DD MMM YYYY")}
                    </p>
                    {pieData.length === 0 ? (
                      <div className="no-item-message">
                        There is currently no usage data available.
                      </div>
                    ) : (
                      <ResponsiveContainer width="100%" height={340}>
                        <PieChart>
                          <Pie
                            data={pieData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="45%"
                            outerRadius={110}
                            isAnimationActive={false}
                          >
                            {pieData.map((slice) => (
                              <Cell key={slice.name} fill={slice.color} />
                            ))}
                          </Pie>
                          <Tooltip content={renderPieTooltip} />
                          <Legend
                            verticalAlign="bottom"
                            iconType="square"
                            wrapperStyle={{ fontSize: "12px" }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </div>
              </div>

              <div className="col-md-7 mb-4">
                <div className="card">
                  <div className="card-body">
                    <h5 className="text-center mb-1">
                      Water usage by unit, recent trend
                    </h5>
                    <p className="text-center text-muted small mb-3">
                      Average kL/d over each window
                    </p>
                    <ResponsiveContainer width="100%" height={trendChartHeight}>
                      <BarChart
                        layout="vertical"
                        data={trendData}
                        margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                        barGap={2}
                      >
                        <CartesianGrid horizontal={false} stroke="#eceff3" />
                        <XAxis
                          type="number"
                          tick={{ fontSize: 12 }}
                          tickFormatter={(value) => formatNumber(value)}
                        />
                        <YAxis
                          type="category"
                          dataKey="name"
                          width={trendAxisWidth}
                          interval={0}
                          tick={{ fontSize: 12 }}
                        />
                        <Tooltip content={renderTrendTooltip} />
                        <Legend
                          verticalAlign="bottom"
                          iconType="square"
                          wrapperStyle={{ fontSize: "12px" }}
                        />
                        {TREND_SERIES.map((series) => (
                          <Bar
                            key={series.key}
                            dataKey={series.key}
                            name={series.name}
                            fill={series.color}
                            isAnimationActive={false}
                          />
                        ))}
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
            </div>

            {/* Units table — hidden for now, kept for reference.
            <div className="table-responsive">
              <table className="table customer-list">
                <thead>
                  <tr>
                    <th scope="col">Unit</th>
                    <th scope="col">Meter</th>
                    <th scope="col" className="text-right">
                      Avg daily (kL/d)
                    </th>
                    <th scope="col" className="text-right">
                      Total (kL)
                    </th>
                    <th scope="col" className="text-right">
                      Current week
                    </th>
                    <th scope="col" className="text-right">
                      Previous week
                    </th>
                    <th scope="col" className="text-right">
                      3 month average
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ fontWeight: 600 }}>
                    <td>
                      <span
                        className="d-inline-block mr-2"
                        style={{
                          width: "10px",
                          height: "10px",
                          background: MAIN_COLOR,
                        }}
                      />
                      {propOr("Main meter", "name", main)}
                    </td>
                    <td>{propOr("", "meter_id", main)}</td>
                    <td className="text-right">
                      {formatNumber(round2(main.avg_daily))}
                    </td>
                    <td className="text-right">
                      {formatNumber(round2(main.total))}
                    </td>
                    <td className="text-right">
                      {formatNumber(round2(main.current_week))}
                    </td>
                    <td className="text-right">
                      {formatNumber(round2(main.previous_week))}
                    </td>
                    <td className="text-right">
                      {formatNumber(round2(main.three_month_average))}
                    </td>
                  </tr>

                  {units.map((unit, index) => (
                    <tr key={unit.id}>
                      <td>
                        <span
                          className="d-inline-block mr-2"
                          style={{
                            width: "10px",
                            height: "10px",
                            background: unitColor(index),
                          }}
                        />
                        <Link to={`/customer/${unit.id}/usage`}>
                          {unit.name}
                        </Link>
                      </td>
                      <td>{propOr("", "meter_id", unit)}</td>
                      <td className="text-right">
                        {formatNumber(round2(unit.avg_daily))}
                      </td>
                      <td className="text-right">
                        {formatNumber(round2(unit.total))}
                      </td>
                      <td className="text-right">
                        {formatNumber(round2(unit.current_week))}
                      </td>
                      <td className="text-right">
                        {formatNumber(round2(unit.previous_week))}
                      </td>
                      <td className="text-right">
                        {formatNumber(round2(unit.three_month_average))}
                      </td>
                    </tr>
                  ))}

                  <tr>
                    <td>
                      <span
                        className="d-inline-block mr-2"
                        style={{
                          width: "10px",
                          height: "10px",
                          background: UNACCOUNTED_COLOR,
                        }}
                      />
                      {propOr("", "name", unaccounted)}
                    </td>
                    <td />
                    <td className="text-right">
                      {formatNumber(round2(unaccounted.avg_daily))}
                    </td>
                    <td className="text-right">
                      {formatNumber(round2(unaccounted.total))}
                    </td>
                    <td className="text-right">
                      {formatNumber(round2(unaccounted.current_week))}
                    </td>
                    <td className="text-right">
                      {formatNumber(round2(unaccounted.previous_week))}
                    </td>
                    <td className="text-right">
                      {formatNumber(round2(unaccounted.three_month_average))}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            */}
          </>
        )}
      </div>
    </div>
  );
};

export default CustomerGroupPage;
